import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { ProductItem } from '../../model/product-item';
import { ProductsPage } from '../../model/products-page';
import { SeoService } from '../../service/seo.service';
import { PaginationComponent } from '../product/pagination.component';
import { ProductItemComponent } from '../product/product-item.component';
import { ProductSortingComponent } from '../product/product-sorting.component';
import { ProductsFilterComponent } from '../product/products-filter.component';
import { AdminService, adminFailure } from '../../service/admin.service';
import { PageFailure } from '../../service/page-failure';
import { WebsitePageComponent } from './website-page.component';

/**
 * The `products` template, and the `website-page` template it falls back to: a category code can
 * name either a product category or an editorial page, and the API decides which.
 */
@Component({
  selector: 'products-page',
  imports: [
    RouterLink, ProductsFilterComponent, ProductSortingComponent, ProductItemComponent,
    PaginationComponent, WebsitePageComponent
  ],
  host: {
    style: 'display: contents'
  },
  template: `
    @if (data().websitePage; as websitePage) {
      <website-page [page]="websitePage" />
    } @else {
      <!-- Title Page -->
      <section class="bg-title-page p-t-15 p-b-16 flex-col-c-m"
        [style.background-image]="data().categoryPhoto ? 'url(' + data().categoryPhoto + ')' : null">
        <h1 class="l-text2 t-center">
          {{data().heading}}
        </h1>
        <p class="m-text13 t-center">
          {{data().slogan}}
        </p>
      </section>

      <!-- Content page -->
      <section class="bgwhite pt-3 pt-md-5 p-b-65">
        <div class="container">
          <div class="row">
            <div class="col-md-4 col-lg-3 pb-3 pb-md-5">
              <products-filter [filter]="data().filter" [searchText]="data().search" />
            </div>

            <div class="col-md-8 col-lg-9 pb-3 pb-md-5" id="product-list">
              <div class="flex-sb-m flex-w pb-1 pb-md-4">
                <product-sorting [sort]="data().sort" [many]="products().length > 1" />
                @if (totalPages() > 1) {
                  <span class="s-text8 p-t-5 p-b-5">
                    cторінка {{data().page!.number + 1}} з {{totalPages()}}
                  </span>
                }
              </div>

              <!-- Product -->
              <div class="row">
                @if (isAdmin() && data().currentCategory) {
                  <div class="col-6 col-md-6 col-lg-4 p-b-50">
                    <div class="block2 product-item">
                      <div class="block2-img wrap-pic-w of-hidden pos-relative hov-img-zoom">
                        <button type="button" class="admin-new-product p-5" aria-label="Додати товар"
                          [disabled]="creating()" (click)="createProduct()">
                          <img src="/images/Plus_font_awesome.svg" alt="">
                        </button>
                      </div>
                      @if (failure(); as failure) {
                        <p class="s-text5 text-danger p-t-10">{{failure}}</p>
                      }
                    </div>
                  </div>
                }
                @for (product of products(); track product.id) {
                  <div class="col-6 col-md-6 col-lg-4 p-b-50">
                    <product-item [product]="product" />
                  </div>
                } @empty {
                  <div class="col-12 p-b-50 text-center">
                    @if (data().filter?.activeFilter) {
                      Не знайдено жодного товару із заданими параметрами фільтру.<br>
                      <a routerLink="/products">Натисніть тут, щоб повернутись на сторінку товарів</a>.
                    } @else if (data().currentCategory) {
                      На жаль, всі товари у цій категорії продано.<br>
                      <a routerLink="/products">Натисніть тут, щоб повернутись на сторінку товарів</a>.
                    }
                  </div>
                }
              </div>

              <pagination [page]="data().page" />
            </div>
          </div>
        </div>
      </section>
    }
    `
})
export class ProductsPageComponent {

  readonly data = input.required<ProductsPage>();

  private admin = inject(AdminService);
  private router = inject(Router);
  private pageFailure = inject(PageFailure);

  readonly isAdmin = this.admin.isAdmin;
  readonly failure = signal<string | undefined>(undefined);
  /** The API makes a new draft for every call, so a double click would make two. */
  readonly creating = signal(false);

  readonly products = computed(() => this.data().page?.content ?? []);
  readonly totalPages = computed(() => this.data().page?.totalPages ?? 0);

  constructor() {
    const seo = inject(SeoService);
    effect(() => {
      const data = this.data();
      // One instance serves every category and page, so a refusal belongs to the page it was on.
      this.failure.set(undefined);
      // The editorial fallback carries the ' | shop' title suffix; the product list does not.
      seo.apply(data, !!data.websitePage);
      seo.openGraph(undefined);
    });
  }

  async createProduct() {
    if (this.creating()) {
      return;
    }
    this.creating.set(true);
    try {
      await this.create();
    } finally {
      this.creating.set(false);
    }
  }

  private async create() {
    this.failure.set(undefined);
    let created: ProductItem;
    try {
      created = await this.admin.createProduct(this.data().currentCategory!);
    } catch (error) {
      this.failure.set(adminFailure(error, 'Не вдалося створити товар. Спробуйте ще раз.'));
      return;
    }
    try {
      await this.router.navigateByUrl(created.uri);
    } catch {
      // It exists by now, and "try again" would only make another one
      this.pageFailure.message.set(undefined);
      this.failure.set('Товар створено, але його сторінку не вдалося відкрити. Він є в ієрархії товарів.');
    }
  }

}
